/*
 * CHAG delivery postcode check (cart drawer + /cart page). No app.
 *
 * Shopify shipping zones stop at England / Scotland / Wales / NI, so postcodes
 * DHL cannot reach next-day are stopped here, before checkout. Excluded = DHL
 * zones C and D (source: reference/dhl-postcode-zones.csv, 2026-09-17):
 *   C  Northern Ireland (all BT)
 *   D  Scottish Highlands and Islands, Channel Islands, Isle of Man
 * Isle of Wight (IOW) and Isles of Scilly (IOS) are separate DHL zones and are
 * NOT excluded.
 *
 * The postcode stays in the browser (sessionStorage); since 2026-09-21 it is no
 * longer written to the cart attribute "Delivery postcode", because ShipStation
 * prints every order attribute on the packing slip and nothing read this one.
 * A cart from before the change may still carry it: it is cleared on sight.
 * This is a soft block: express checkout buttons and a direct /checkout URL
 * bypass it. It FAILS OPEN: any script error lets checkout through.
 */

(function () {
  // Postcode areas where every district is zone C or D.
  const EXCLUDED_AREAS = ['BT', 'GY', 'HS', 'IM', 'IV', 'JE', 'KW', 'ZE'];

  // Areas that are only partly excluded: inclusive district-number ranges.
  const EXCLUDED_DISTRICTS = {
    AB: [[31, 38], [41, 56]],
    KA: [[27, 28]],
    PA: [[21, 50], [60, 78], [80, 80]],
    PH: [[4, 44], [49, 50]],
  };

  const ATTRIBUTE = 'Delivery postcode';
  const STORAGE_KEY = 'chag-delivery-postcode';
  const OUTWARD = /^[A-Z]{1,2}\d[A-Z\d]?$/;
  const FULL = /^([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{2})$/;

  const MESSAGES = {
    empty: 'Please enter the delivery postcode so we can check we deliver there.',
    invalid: 'Please enter a valid UK postcode, for example TR1 2AA.',
    excluded:
      "Sorry, we can't deliver to {code}. Our next-day delivery service doesn't cover Northern Ireland, the Scottish Highlands and Islands, the Channel Islands or the Isle of Man.",
    ok: 'Good news, we deliver to {code}.',
  };

  /*
   * Returns { outward, formatted, complete } or null if it is not a UK postcode.
   * `complete` is false for an outward code on its own ("IV2") or a postcode
   * that is still being typed ("PH1 0").
   */
  function parsePostcode(raw) {
    const text = String(raw || '').toUpperCase().trim();
    const compact = text.replace(/[^A-Z0-9]/g, '');
    if (!compact) return null;

    const full = compact.match(FULL);
    if (full) return { outward: full[1], formatted: full[1] + ' ' + full[2], complete: true };

    // "PH1 0": the space tells us where the outward code ends.
    const parts = text.split(/\s+/);
    if (parts.length === 2 && OUTWARD.test(parts[0]) && /^\d[A-Z]{0,2}$/.test(parts[1])) {
      return { outward: parts[0], formatted: parts[0], complete: false };
    }

    if (OUTWARD.test(compact)) return { outward: compact, formatted: compact, complete: false };
    return null;
  }

  function isExcluded(outward) {
    const match = outward.match(/^([A-Z]{1,2})(\d{1,2})/);
    if (!match) return false;
    const area = match[1];
    const district = parseInt(match[2], 10);
    if (EXCLUDED_AREAS.includes(area)) return true;
    return (EXCLUDED_DISTRICTS[area] || []).some(([from, to]) => district >= from && district <= to);
  }

  /* 'empty' | 'invalid' | 'excluded' | 'ok' */
  function evaluate(raw) {
    if (!String(raw || '').trim()) return { state: 'empty' };
    const parsed = parsePostcode(raw);
    if (!parsed) return { state: 'invalid' };
    return { state: isExcluded(parsed.outward) ? 'excluded' : 'ok', parsed };
  }

  // Under node (no DOM) export the pure functions so the list can be tested
  // against the DHL file: reference/test_delivery_postcode_check.js.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { parsePostcode, isExcluded, evaluate };
    return;
  }

  if (customElements.get('delivery-postcode-check')) return;

  customElements.define(
    'delivery-postcode-check',
    class DeliveryPostcodeCheck extends HTMLElement {
      connectedCallback() {
        try {
          this.input = this.querySelector('[data-dp-input]');
          this.messageEl = this.querySelector('[data-dp-message]');
          if (!this.input) return;

          // The drawer re-renders on every cart change. Bring back a postcode
          // that was typed but never saved (excluded or incomplete).
          if (!this.input.value) {
            try {
              this.input.value = sessionStorage.getItem(STORAGE_KEY) || '';
            } catch (e) {
              /* storage blocked: nothing to restore */
            }
          }

          // Legacy cart: keep the prefilled postcode for this session, then
          // take the attribute off the cart.
          if (this.hasAttribute('data-dp-legacy')) {
            try {
              if (this.input.value) sessionStorage.setItem(STORAGE_KEY, this.input.value);
            } catch (e) {
              /* storage blocked */
            }
            this.save();
          }

          this.input.addEventListener('input', () => this.check(false));
          this.input.addEventListener('change', () => this.check(true));
          this.input.addEventListener('blur', () => this.check(true));
          this.ready = true;
          if (this.input.value) this.check(true);
        } catch (err) {
          this.ready = false;
          if (window.console) console.error('[delivery-postcode-check] init failed; checkout not gated', err);
        }
      }

      /*
       * `final` is false while typing: only a complete postcode is judged then,
       * so "PH10" on the way to "PH1 0AA" never flashes a refusal.
       */
      check(final) {
        const raw = this.input.value;
        try {
          sessionStorage.setItem(STORAGE_KEY, raw);
        } catch (e) {
          /* storage blocked */
        }

        const result = evaluate(raw);
        if (!final && !(result.parsed && result.parsed.complete)) {
          this.show(null);
          return result;
        }
        if (result.state === 'empty') {
          this.show(null);
          return result;
        }

        this.show(result.state, result.parsed && result.parsed.outward);
        if (result.state === 'ok' && final) {
          if (result.parsed.complete) this.input.value = result.parsed.formatted;
          this.save(result.parsed.formatted);
        }
        return result;
      }

      /* Called by the checkout guard. Returns true when checkout may proceed. */
      allowCheckout() {
        if (!this.ready) return true;
        const result = this.check(true);
        if (result.state === 'ok') return true;
        if (result.state === 'empty') this.show('empty');
        this.input.focus();
        return false;
      }

      show(state, code) {
        if (!this.messageEl) return;
        this.classList.toggle('dp--ok', state === 'ok');
        this.classList.toggle('dp--blocked', Boolean(state) && state !== 'ok');
        this.input.setAttribute('aria-invalid', state && state !== 'ok' ? 'true' : 'false');
        if (!state) {
          this.messageEl.hidden = true;
          this.messageEl.textContent = '';
          return;
        }
        this.messageEl.textContent = MESSAGES[state].replace('{code}', code || '');
        this.messageEl.hidden = false;
      }

      /*
       * Nothing is saved to the cart any more. The only request left is the
       * one-off removal of the attribute from a cart started before 2026-09-21
       * (an empty value removes a cart attribute).
       */
      save() {
        if (this.cleared || !this.hasAttribute('data-dp-legacy')) return;
        this.cleared = true;
        const body = JSON.stringify({ attributes: { [ATTRIBUTE]: '' } });
        const cfg =
          typeof fetchConfig === 'function'
            ? fetchConfig()
            : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
        const url = (typeof routes !== 'undefined' && routes.cart_update_url) || '/cart/update.js';
        fetch(url, { ...cfg, body })
          .then(() => this.removeAttribute('data-dp-legacy'))
          .catch(() => {
            /* Non-fatal: the attribute rides along on this one order. */
          });
      }
    }
  );

  /*
   * Checkout guard. Capture phase on the document so it runs before the
   * theme's own handlers and survives drawer re-renders. The date picker owns
   * the button's disabled state; this only ever cancels the click.
   */
  function guard(event, button) {
    try {
      const scope = button.closest('cart-drawer');
      const check = Array.from(document.querySelectorAll('delivery-postcode-check')).find(
        (el) => el.closest('cart-drawer') === scope
      );
      if (!check || check.allowCheckout()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    } catch (err) {
      if (window.console) console.error('[delivery-postcode-check] guard failed; checkout allowed', err);
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest && event.target.closest('[data-dd-checkout]');
      if (button) guard(event, button);
    },
    true
  );

  // Enter inside a cart field submits the form with the checkout button.
  document.addEventListener(
    'submit',
    (event) => {
      const button = event.submitter;
      if (button && button.matches && button.matches('[data-dd-checkout]')) guard(event, button);
    },
    true
  );
})();
